import React, {
  ComponentProps,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useIntl } from 'react-intl-next';
import { useSelector } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import ComponentIcon from '@atlaskit/icon/glyph/component';
/* eslint frontbucket-patterns/no-old-tooltip: "warn" */
import Tooltip from '@atlaskit/tooltip';
import { FeatureKeys } from '@atlassian/bitbucket-features';
import {
  BitbucketChooseOrCreateComponentEmptyState,
  CompassError,
  ComponentCardSearchList,
  Product,
} from '@atlassiansox/compass-component-card-list';
import {
  CompassComponent,
  UIAnalyticsEvent,
} from '@atlassiansox/compass-component-card-list/dist/types';

import { SectionErrorBoundary } from 'src/components/error-boundary';
import { Expander } from 'src/components/sidebar';
import { useDjangoFeature } from 'src/hooks/use-django-feature';
import {
  getCurrentRepository,
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
} from 'src/selectors/repository-selectors';
import {
  publishOperationalEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { ComponentCardPermissionErrorState } from './error-states/compass-card-permission-error-state';
import { useCompassCloudIdForRepository, useCompassSiteInfo } from './hooks';
import messages from './messages';

type CompassSearchComponentQuery = React.ComponentProps<
  typeof ComponentCardSearchList
>['query'];

const CompassCardInternal = (props: {
  isCollapsed?: boolean;
}): JSX.Element | null => {
  const { isCollapsed } = props;
  const { formatMessage } = useIntl();
  const [componentCount, setComponentCount] = useState<number>();
  const [componentLinked, setComponentLinked] = useState<CompassComponent>();

  const {
    cloudId,
    isLoading: cloudIdLoading,
    error: cloudIdError,
  } = useCompassCloudIdForRepository('repositoryOwner', 'repositorySlug');
  const {
    siteUrl,
    loading: siteInfoLoading,
    error: siteInfoError,
  } = useCompassSiteInfo(cloudId);

  const isLoading = cloudIdLoading || siteInfoLoading;
  const [componentError, setComponentError] = useState<Error>();
  const error = cloudIdError || siteInfoError || componentError;

  const owner = useSelector(getCurrentRepositoryOwnerName) || '';
  const slug = useSelector(getCurrentRepositorySlug) || '';
  const repository = useSelector(getCurrentRepository);

  const repoSlug = `bbc+${owner}+${slug}`;
  const query: CompassSearchComponentQuery = useMemo(
    () => ({
      first: 50,
      fieldFilters: [
        {
          name: 'repo_slugs',
          filter: {
            in: [repoSlug],
          },
        },
      ],
    }),
    [repoSlug]
  );

  const handleComponentsLoaded = useCallback(
    (componentSearchResult: { totalCount?: number }) => {
      setComponentCount(componentSearchResult.totalCount);
    },
    [setComponentCount]
  );

  const handleError = useCallback(
    (e: Error, analyticsEvent: UIAnalyticsEvent) => {
      publishOperationalEvent(analyticsEvent);
      setComponentError(e);

      if (e.message !== CompassError.PERMISSION_DENIED) {
        // Send unexpected errors to Sentry
        captureExceptionWithTags(e, { component: 'compass-card' });
      }
    },
    []
  );

  if (!cloudId || !siteUrl) {
    return null;
  }

  let componentCardContents: ReactNode;
  if (error) {
    if (error.message === CompassError.PERMISSION_DENIED) {
      // display an alternative view for PERMISSION_DENIED
      componentCardContents = (
        <ComponentCardPermissionErrorState cloudId={cloudId} />
      );
    } else {
      return null;
    }
  } else {
    if (!repository) {
      return null;
    }

    const repositoryFullPath = repository.links.html.href;

    componentCardContents = (
      <ComponentCardSearchList
        cloudId={cloudId}
        query={query}
        siteUrl={siteUrl}
        onComponentsLoaded={handleComponentsLoaded}
        componentLinked={componentLinked}
        emptyView={
          <BitbucketChooseOrCreateComponentEmptyState
            repositoryUrl={repositoryFullPath}
            onAnalyticsEvent={publishUiEvent}
            cloudId={cloudId}
            product={Product.Bitbucket}
            setComponentLinked={setComponentLinked}
          />
        }
        onError={handleError}
        onAnalyticsEvent={publishUiEvent}
        sourceContext="bbc"
        product={Product.Bitbucket}
      />
    );
  }

  if (isCollapsed) {
    return (
      <Tooltip
        testId="compass-card-collapsed-tooltip"
        content={formatMessage(messages.collapsedTabHeader)}
      >
        <Button
          testId="compass-card-collapsed-tooltip-btn"
          appearance="subtle"
          iconBefore={
            <ComponentIcon label={formatMessage(messages.collapsedIconLabel)} />
          }
        />
      </Tooltip>
    );
  }

  return (
    <Expander
      icon={
        <ComponentIcon
          label={
            isCollapsed
              ? formatMessage(messages.collapsedIconLabel)
              : formatMessage(messages.expandedIconLabel)
          }
        />
      }
      label={formatMessage(messages.tabHeader, {
        componentCount,
        strong: (str: string) => <strong>{str}</strong>,
      })}
      isLoading={isLoading}
      onChange={event => {
        publishUiEvent({
          action: event.isCollapsed ? 'closed' : 'expanded',
          actionSubject: 'compassCardExpander',
          source: 'componentCardSearchList',
          attributes: {
            cloudId,
            repoSlug,
          },
        });
      }}
      isPanelBodyOverflowVisible
      panelBodyPadding="0"
      dataTestId="compass-card"
    >
      {componentCardContents}
    </Expander>
  );
};

export const CompassCard = (
  props: ComponentProps<typeof CompassCardInternal> & { isLoggedIn?: boolean }
): JSX.Element | null => {
  const hasFeature = useDjangoFeature(FeatureKeys.compassXFlowUI);
  const { isLoggedIn } = props;
  if (!hasFeature || !isLoggedIn) {
    return null;
  }
  return (
    <SectionErrorBoundary sentryComponentId="compass-card">
      <CompassCardInternal {...props} />
    </SectionErrorBoundary>
  );
};
